/**
 * Client-side analytics event tracker
 *
 * Fires events to Google Analytics (gtag) and Meta Pixel (fbq) when present.
 * Falls back to console.log in dev mode.
 */

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
    fbq?: (...args: unknown[]) => void
  }
}

const PRODUCTION_ANALYTICS_HOSTS = new Set(['farzadbayat.com', 'www.farzadbayat.com'])
const GA_MEASUREMENT_ID = /^G-[A-Z0-9]+$/

export function installGoogleAnalytics(options?: {
  hostname?: string
  measurementId?: string
}): void {
  if (typeof document === 'undefined') return

  const hostname = options?.hostname ?? window.location.hostname
  const measurementId = (
    options?.measurementId ??
    process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID ??
    ''
  ).trim()

  if (!PRODUCTION_ANALYTICS_HOSTS.has(hostname)) return
  if (!GA_MEASUREMENT_ID.test(measurementId)) return
  if (typeof window.gtag === 'function') return

  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag() {
    // gtag.js reads the Arguments object. A rest array is a different command shape.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments as unknown)
  }
  window.gtag('js', new Date())
  window.gtag('config', measurementId)

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
  document.head.appendChild(script)
}

export type AnalyticsEvent =
  | 'chat_started'
  | 'email_captured'
  | 'discovery_report_generated'
  | 'booking_confirmed'
  | 'contact_form_submitted'
  | 'follow_up_scheduled'

export function trackEvent(
  name: AnalyticsEvent,
  properties?: Record<string, unknown>
): void {
  if (typeof window === 'undefined') return

  // Google Analytics
  if (window.gtag) {
    try {
      window.gtag('event', name, properties)
    } catch {
      // Silently fail — analytics should never break the app
    }
  }

  // Meta Pixel
  if (window.fbq) {
    try {
      window.fbq('trackCustom', name, properties)
    } catch {
      // Silently fail
    }
  }

  // Dev logging — use console.info in DEV only (eslint: no-console allows console.info for diagnostics)
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.info('[analytics]', name, properties)
  }
}
